import { CommonModule } from "@angular/common";
import { HttpClientModule } from "@angular/common/http";
import { APP_INITIALIZER, Injector, NgModule, isDevMode } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatSelectModule } from "@angular/material/select";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { StoreModule } from "@ngrx/store";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { TranslateService } from "@ngx-translate/core";
import { LucideAngularModule } from "lucide-angular";
import { NgChartsModule } from "ng2-charts";
import { firstValueFrom } from "rxjs";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AddOrganizationPageModule } from "./epicare-public/administration/views/add-organization-page/add-organization-page.module";
import { AdministrationLayoutModule } from "./epicare-public/administration/views/administration-layout/administration-layout.module";
import { AdministrationPageModule } from "./epicare-public/administration/views/administration-page/administration-page.module";
import { InvitationModule } from "./epicare-public/administration/views/invitation-page/invitation-page.module";
import { ChangeMemberRoleDialogModule } from "./epicare-public/administration/views/change-member-role-dialog/change-member-role-dialog.module";
import { InviteMemberDialogModule } from "./epicare-public/administration/views/invite-member-dialog/invite-member-dialog.module";
import { RemoveMemberDialogModule } from "./epicare-public/administration/views/remove-member-dialog/remove-member-dialog.module";
import { LinkPageModule } from "./epicare-public/administration/views/link-page/link-page.module";
import { OrganizationPageModule } from "./epicare-public/administration/views/organization-page/organization-page.module";
import { NotFoundPageModule } from "./epicare-public/error/views/not-found-page/not-found-page.module";
import { HomePageModule } from "./epicare-public/home/views/home-page/home-page.module";
import { MetadataPageModule } from "./epicare-public/home/views/metadata-page/metadata-page.module";
import { SamplesPageModule } from "./epicare-public/home/views/samples-page/samples-page.module";
import { UploadPageModule } from "./epicare-public/home/views/upload-page/upload-page.module";
import { LandingPageModule } from "./epicare-public/landing/views/landing-page/landing-page.module";
import { LoginDialogModule } from "./epicare-public/login/views/login-dialog/login-dialog.module";
import { LoginFormModule } from "./epicare-public/login/views/login-page/components/login-form/login-form.module";
import { LoginPageModule } from "./epicare-public/login/views/login-page/login-page.module";
import { ResetPasswordDialogModule } from "./epicare-public/login/views/reset-password-dialog/reset-password-dialog.module";
import { ResetPasswordPageModule } from "./epicare-public/login/views/reset-password-page/reset-password-page.module";
import { AccountMenuComponent } from "./shared/components/account-menu/account-menu.component";
import { FooterComponent } from "./shared/components/footer/footer.component";
import { OrganizationSwitcherModule } from "./shared/components/organization-switcher/organization-switcher.module";
import { ToolbarComponent } from "./shared/components/toolbar/toolbar.component";
import { UserProfileModule } from "./shared/components/user-profile/user-profile.module";
import { IconsModule } from "./shared/icons.module";
import { ConfigService } from "./shared/services/config.service";
import { PermissionLoaderService } from "./shared/services/permission-loader.service";
import { SessionService } from "./shared/services/session.service";
import { CommonTranslateModule } from "./shared/utils/common-translate.module";

const materialModules = [
  MatButtonModule,
  MatIconModule,
  MatMenuModule,
  MatSelectModule,
  MatToolbarModule,
  MatTooltipModule,
  MatDialogModule,
  MatSnackBarModule,
];

/**
 * Factory for the `APP_INITIALIZER` provider that boots the app's core async dependencies
 * in a strict, guaranteed order.
 *
 * Order matters here: `TranslateService` (and its loader chain, `CustomI18nTranslateLoader` ->
 * `HttpClient`) must not be constructed until `ConfigService.load()` has fully resolved, since
 * `ApiService` relies on config values (e.g. `apiUrl`) being available.
 *
 * To achieve this, `TranslateService` and `SessionService` are intentionally **not** listed
 * in `deps`. Angular resolves all `deps` synchronously when building the list of
 * `APP_INITIALIZER` factories, which would instantiate `TranslateService` immediately - before
 * `configService.load()` has finished, even though this factory itself runs later. Instead,
 * only the generic `Injector` is injected, and the actual dependent services are resolved
 * manually via `injector.get(...)` inside the async function body, strictly after the
 * `await configService.load()` call. This guarantees the config is fully loaded before
 * anything that depends on it is created.
 *
 * @param configService Service responsible for loading app configuration (e.g. `apiUrl`) before anything else that depends on it is instantiated.
 * @param injector The root injector, used to lazily resolve `TranslateService`, `SessionService` and `PermissionLoaderService` only after config has loaded, avoiding premature instantiation of their dependency chain.
 * @return A factory function invoked by Angular's `APP_INITIALIZER` mechanism. The returned promise resolves once both config loading and the initial translation setup are complete; Angular delays app bootstrap until it resolves.
 */
function initApp(configService: ConfigService, injector: Injector): () => Promise<void> {
  return async () => {
    // Step 1: load app configuration first (e.g. apiUrl) — everything else depends on it.
    await configService.load();

    // Step 2: only now resolve TranslateService and SessionService, guaranteeing config is
    // already available by the time anything depending on it (e.g. ApiService) is built.
    const translateService = injector.get(TranslateService);
    const sessionService = injector.get(SessionService);
    // Instantiated now so it listens to logins from the start (e.g. on /login, /invitation or
    // /link), which already come with the permissions it would otherwise fetch on the next navigation
    injector.get(PermissionLoaderService);

    translateService.getBrowserLang();
    translateService.setDefaultLang("en"); // this language will be used as a fallback when a translation isn't found in the current language
    // the lang to use, if the lang isn't available, it will use the current loader to get them
    if (sessionService.userLang) {
      await firstValueFrom(translateService.use(sessionService.userLang));
    } else {
      await firstValueFrom(translateService.use(translateService.getBrowserLang() || translateService.getDefaultLang()));
    }
  };
}

@NgModule({
  declarations: [AccountMenuComponent, AppComponent, ToolbarComponent, FooterComponent],
  imports: [
    CommonModule,
    NotFoundPageModule,
    HomePageModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientModule,
    StoreModule.forRoot({}, {}),
    CommonTranslateModule,
    UploadPageModule,
    MetadataPageModule,
    SamplesPageModule,
    LoginPageModule,
    LandingPageModule,
    LoginFormModule,
    LoginDialogModule,
    AdministrationLayoutModule,
    AdministrationPageModule,
    AddOrganizationPageModule,
    OrganizationPageModule,
    InviteMemberDialogModule,
    ChangeMemberRoleDialogModule,
    RemoveMemberDialogModule,
    InvitationModule,
    LinkPageModule,
    UserProfileModule,
    OrganizationSwitcherModule,
    ResetPasswordDialogModule,
    ResetPasswordPageModule,
    NgChartsModule,
    ...materialModules,
    BrowserModule,
    IconsModule,
    LucideAngularModule,
  ],
  providers: [
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [ConfigService, Injector],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
